import React, { useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import styled from "styled-components";
import FeatherIcon from "feather-icons-react";
import { colors } from "app/popleads/components/ui-kit/colors";
import Button from "app/popleads/components/ui-kit/Button";
import useGetCainNotifications from "app/popleads/hooks/useGetCainNotifications";
import useMarkCainNotificationAsRead from "app/popleads/hooks/useMarkCainNotificationAsRead";

const ToastContent = styled.div`
    min-width: 0;
    overflow-wrap: anywhere;
    word-break: break-word;
`;

const ToastTitle = styled.div`
    font-size: 14px;
    font-weight: 600;
    color: ${colors.neutral[900]};
    margin-bottom: 2px;
`;

const ToastMessage = styled.div`
    font-size: 13px;
    color: ${colors.neutral[700]};
`;

const CLOSE_ICON_SIZE = 16;
const CLOSE_BUTTON_PADDING = 2;
const CLOSE_BUTTON_GAP = 8;
// Horizontal space the close button takes beside the toast body.
const CLOSE_BUTTON_COLUMN = CLOSE_BUTTON_GAP + CLOSE_ICON_SIZE + CLOSE_BUTTON_PADDING;
// Side padding of the kit Button at size="small".
const SMALL_BUTTON_PADDING = 16;

/*
 * react-toastify's built-in close button is only readable under `theme="light"`;
 * this app's <ToastContainer theme="colored" /> (src/app/App.js) leaves it white-on-white
 * for a plain (non-colored-type) toast like this one, so it renders invisible rather than
 * absent. Rendering our own icon sidesteps that theme/type coupling entirely.
 */
const CloseButton = styled.button`
    background: transparent;
    border: none;
    cursor: pointer;
    padding: ${CLOSE_BUTTON_PADDING}px;
    margin: -${CLOSE_BUTTON_PADDING}px -${CLOSE_BUTTON_PADDING}px 0 ${CLOSE_BUTTON_GAP}px;
    color: ${colors.neutral[500]};
    display: flex;
    align-items: flex-start;

    &:hover {
        color: ${colors.neutral[800]};
    }
`;

/*
 * The toast body stops where the close button column starts, and the kit Button pads its
 * label. Pulling the footer right by both puts "Dismiss all" flush with the close icon's
 * right edge instead of floating inside the body.
 */
const ToastFooter = styled.div`
    display: flex;
    justify-content: flex-end;
    margin-top: 8px;
    margin-right: -${CLOSE_BUTTON_COLUMN + SMALL_BUTTON_PADDING}px;
`;

const renderCloseButton = ({ closeToast }) => (
    <CloseButton type="button" onClick={closeToast} aria-label="Close">
        <FeatherIcon icon="x" size={CLOSE_ICON_SIZE} />
    </CloseButton>
);

const toastIdFor = (notificationId) => `cain-notification-${notificationId}`;

/*
 * Toast content renders inside the ToastContainer, not under this component, so it cannot
 * read our state or context. This store is the bridge: it tracks which notifications are
 * still on screen so every toast can show the live "Dismiss all" count.
 */
const createOpenToastStore = () => {
    const ids = new Set();
    const listeners = new Set();
    const notify = () => listeners.forEach((listener) => listener(ids.size));
    return {
        add: (id) => {
            ids.add(id);
            notify();
        },
        remove: (id) => {
            ids.delete(id);
            notify();
        },
        size: () => ids.size,
        ids: () => [...ids],
        subscribe: (listener) => {
            listeners.add(listener);
            return () => listeners.delete(listener);
        },
    };
};

const CainToastContent = ({ notification, store, onDismissAll }) => {
    const [openCount, setOpenCount] = useState(store.size());
    useEffect(() => store.subscribe(setOpenCount), [store]);

    return (
        <ToastContent>
            <ToastTitle>{notification.title}</ToastTitle>
            <ToastMessage>{notification.message}</ToastMessage>
            {openCount > 1 && (
                <ToastFooter>
                    <Button variant="l3rnTech" buttonStyle="borderless" size="small" onClick={onDismissAll}>
                        Dismiss all ({openCount})
                    </Button>
                </ToastFooter>
            )}
        </ToastContent>
    );
};

function ExtensionCainNotificationToasts() {
    const { data } = useGetCainNotifications({ isRead: 0, page: 1, rowsPerPage: 20 });
    const { markAsRead } = useMarkCainNotificationAsRead();
    const shownIds = useRef(new Set());
    const openToasts = useRef(createOpenToastStore()).current;
    // While set, the toasts closing are part of a dismiss all that was already marked read
    // in one request, so their own onClose must not send a request each.
    const dismissingAll = useRef(false);

    const sendMarkAsRead = (notificationId, options) => {
        markAsRead(notificationId, options).catch(() => {});
    };

    const dismissAll = () => {
        if (dismissingAll.current) return;
        dismissingAll.current = true;
        sendMarkAsRead(undefined, { markAsAllRead: true });
        openToasts.ids().forEach((id) => toast.dismiss(toastIdFor(id)));
    };

    useEffect(() => {
        if (!data?.notifications) return;

        data.notifications.forEach((notification) => {
            if (shownIds.current.has(notification.id)) return;
            shownIds.current.add(notification.id);
            openToasts.add(notification.id);

            toast(<CainToastContent notification={notification} store={openToasts} onDismissAll={dismissAll} />, {
                containerId: "cainNotifications",
                toastId: toastIdFor(notification.id),
                position: "top-center",
                autoClose: false,
                closeOnClick: false,
                draggable: false,
                closeButton: renderCloseButton,
                onClose: () => {
                    openToasts.remove(notification.id);
                    if (dismissingAll.current) {
                        if (openToasts.size() === 0) dismissingAll.current = false;
                        return;
                    }
                    sendMarkAsRead(notification.id, { markAsAllRead: false });
                },
            });
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data]);

    return null;
}

export default ExtensionCainNotificationToasts;
